import { useState } from 'react';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Brain,
  Target,
  Layers,
  Cpu,
  MemoryStick,
  Zap,
  Sparkles,
  CheckCircle2,
  ExternalLink,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
} from 'lucide-react';
import {
  TAO6PretrainedModel,
  TAO6TaskType,
} from '@/mock/gpu-instance';

interface TAO6ModelSelectorProps {
  models: TAO6PretrainedModel[];
  onSelectModel?: (model: TAO6PretrainedModel) => void;
  onViewModelCard?: (model: TAO6PretrainedModel) => void;
  selectedModelId?: string;
  filterTask?: TAO6TaskType;
}

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-4 w-4 text-blue-500" />;
    case 'instance_segmentation':
      return <Box className="h-4 w-4 text-purple-500" />;
    case 'semantic_segmentation':
      return <Shapes className="h-4 w-4 text-cyan-500" />;
    case 'pose_estimation':
      return <PersonStanding className="h-4 w-4 text-orange-500" />;
    case 'action_recognition':
      return <Video className="h-4 w-4 text-pink-500" />;
    case 'ocr':
      return <ScanText className="h-4 w-4 text-amber-500" />;
  }
};

const getTaskBadge = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Target className="h-3 w-3 mr-1" /> Detection
        </Badge>
      );
    case 'image_classification':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Layers className="h-3 w-3 mr-1" /> Classification
        </Badge>
      );
    case 'instance_segmentation':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <Box className="h-3 w-3 mr-1" /> Instance Seg
        </Badge>
      );
    case 'semantic_segmentation':
      return (
        <Badge className="bg-cyan-500/10 text-cyan-600 border-cyan-500/30">
          <Shapes className="h-3 w-3 mr-1" /> Semantic Seg
        </Badge>
      );
    case 'pose_estimation':
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/30">
          <PersonStanding className="h-3 w-3 mr-1" /> Pose
        </Badge>
      );
    case 'action_recognition':
      return (
        <Badge className="bg-pink-500/10 text-pink-600 border-pink-500/30">
          <Video className="h-3 w-3 mr-1" /> Action
        </Badge>
      );
    case 'ocr':
      return (
        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">
          <ScanText className="h-3 w-3 mr-1" /> OCR
        </Badge>
      );
  }
};

const getAccuracyColor = (accuracy: number, metric: 'top1' | 'mAP') => {
  const threshold = metric === 'top1' ? 80 : 50;
  if (accuracy >= threshold + 5) return 'text-green-600';
  if (accuracy >= threshold) return 'text-yellow-600';
  return 'text-orange-600';
};

const TAO6ModelSelector = ({
  models,
  onSelectModel,
  onViewModelCard,
  selectedModelId,
  filterTask: initialFilterTask,
}: TAO6ModelSelectorProps) => {
  const [filterTask, setFilterTask] = useState<string>(initialFilterTask || 'all');
  const [sortBy, setSortBy] = useState<string>('accuracy');

  const filteredModels = models
    .filter((model) => {
      if (filterTask !== 'all' && model.task !== filterTask) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'accuracy':
          return b.accuracy - a.accuracy;
        case 'vram':
          return a.vramRequired - b.vramRequired;
        case 'parameters':
          const parseParams = (p: string) => {
            const num = parseFloat(p);
            if (p.includes('B')) return num * 1000;
            return num;
          };
          return parseParams(a.parameters) - parseParams(b.parameters);
        default:
          return 0;
      }
    });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterTask} onValueChange={setFilterTask}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by task" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="object_detection">Object Detection</SelectItem>
            <SelectItem value="image_classification">Image Classification</SelectItem>
            <SelectItem value="instance_segmentation">Instance Segmentation</SelectItem>
            <SelectItem value="semantic_segmentation">Semantic Segmentation</SelectItem>
            <SelectItem value="pose_estimation">Pose Estimation</SelectItem>
            <SelectItem value="action_recognition">Action Recognition</SelectItem>
            <SelectItem value="ocr">OCR</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="accuracy">Highest Accuracy</SelectItem>
            <SelectItem value="vram">Lowest VRAM</SelectItem>
            <SelectItem value="parameters">Smallest Size</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Model Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredModels.map((model) => (
          <Card
            key={model.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              selectedModelId === model.id ? 'border-primary ring-1 ring-primary' : ''
            }`}
            onClick={() => onViewModelCard?.(model)}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/10 to-blue-500/10">
                    <Brain className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {model.displayName}
                      {selectedModelId === model.id && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {model.description}
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Badge */}
              <div className="mb-3">
                {getTaskBadge(model.task)}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Zap className="h-3 w-3" />
                    {model.accuracyMetric === 'mAP' ? 'mAP' : 'Top-1'}
                  </div>
                  <div className={`font-mono font-bold text-sm ${getAccuracyColor(model.accuracy, model.accuracyMetric)}`}>
                    {model.accuracy.toFixed(1)}%
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <MemoryStick className="h-3 w-3" />
                    VRAM
                  </div>
                  <div className="font-mono font-medium text-sm">
                    {model.vramRequired}GB
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Cpu className="h-3 w-3" />
                    Params
                  </div>
                  <div className="font-mono font-medium text-sm">
                    {model.parameters}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Input</div>
                  <div className="font-mono font-medium text-xs">
                    {model.inputSize}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-3">
                {model.supportsQAT && (
                  <Badge variant="outline" className="text-xs">
                    <Sparkles className="h-3 w-3 mr-1" />
                    QAT
                  </Badge>
                )}
                {model.supportsKD && (
                  <Badge variant="outline" className="text-xs">
                    <Brain className="h-3 w-3 mr-1" />
                    Knowledge Distillation
                  </Badge>
                )}
              </div>

              {/* NGC Path */}
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <code className="text-xs text-muted-foreground truncate">
                  {model.ngcPath}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`https://catalog.ngc.nvidia.com/orgs/nvidia/teams/tao/models/${model.name}`, '_blank');
                  }}
                  title="View on NGC Catalog"
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredModels.length === 0 && (
          <Card className="col-span-2 border-dashed">
            <CardContent className="pt-6 text-center">
              <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No models match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TAO6ModelSelector;
